import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { ApiBaseService } from './api-base.service';
import { SharedService } from './shared.service';

import { MarketBaseModel, PvcMarketBaseModel } from '@models/MarketplaceModel';

import { categories, thematics, stores, vendors, envVariables, giftcards, tickets, products, zones, cashless } from '@shared/mock-data';
import { ProfileImageModel } from '@models/VendorModel';
import { CouponModel } from '@shared/models/CouponModel';
import { EventType } from '@shared/models/Enums';
import { TicketModel } from '@shared/models/TicketModel';

@Injectable()
export class ApiMarketService extends ApiBaseService {

    constructor(public http: HttpClient, public sharedService: SharedService) {
        super(http, sharedService);
    }


    // GET api/admin/market/reads/v1/
    getMarketBase(): Promise<MarketBaseModel> {

        return this.http.get(`${this.baseUrl}/api/admin/market/reads/v1`, this.getHttpOptions())
            .toPromise().then(
                (result: any) => {

                    // JSON retornado por el servidor
                    const categoriesResult: any[] = result.categories.filter(x => x.isActive);
                    const giftcardsResult: any[] = result.giftcards.filter(x => x.isVisible && x.isAvailable);
                    const productsResult: any[] = result.products.filter(x => x.isVisible && x.isAvailable);

                    const market = new MarketBaseModel({
                        // Todos los objetos relacionados
                        categories: categoriesResult,
                        stores: result.stores,
                        vendors: result.vendors,
                        shippingProviders: result.shippingProviders,
                        // Todos los objetos principales
                        giftcards: giftcardsResult,
                        products: [], //productsResult, // UPDATE: Givi 2.5
                    });

                    // Ordenar los productos por nombre
                    market.products = market.products.sort((a, b) => a.name !== b.name ? a.name < b.name ? -1 : 1 : 0);


                    /* OPTIMIZACIÓN: SETEAR LOS OBJETOS RELACIONADOS */
                    // Set the vendor to store
                    market.stores.forEach(x => {
                        x.vendor = market.vendors.find(v => v.id == x.vendorId);
                    });


                    // Set the vendor to giftcard
                    market.giftcards.forEach(x => {
                        if (x.shippingProviderId) x.shippingProvider = market.shippingProviders.find(s => s.id == x.shippingProviderId);
                        x.vendor = market.vendors.find(v => v.id == x.vendorId);

                        // JSON retornado por el servidor
                        let giftcard = giftcardsResult.find(y => y.id == x.id);

                        // Adquirir los datos de la categories
                        x.categories = giftcard?.categoryIds?.map(id => {
                            const cat = market.categories.find(c => c.id == id);
                            return cat;
                        });

                        // Adquirir los datos de la tiendas
                        x.stores = giftcard?.storeIds?.map(id => {
                            const sto = market.stores.find(s => s.id == id);
                            return sto;
                        });
                    });

                    // Set the vendor to product
                    market.products.forEach(x => {
                        if (x.shippingProviderId) x.shippingProvider = market.shippingProviders.find(s => s.id == x.shippingProviderId);
                        x.vendor = market.vendors.find(v => v.id == x.vendorId);

                        // JSON retornado por el servidor
                        const product = productsResult.find(y => y.id == x.id);

                        // Adquirir los datos de la categories
                        x.categories = product?.categoryIds?.map(id => {
                            const cat = market.categories.find(c => c.id == id);
                            return cat;
                        });
                        // Adquirir los datos de la tiendas
                        x.stores = product?.storeIds?.map(id => {
                            const sto = market.stores.find(s => s.id == id);
                            return sto;
                        });
                    });
                    /* OPTIMIZACIÓN: SETEAR LOS OBJETOS RELACIONADOS */


                    /*  ESTO ES PARA MANTENER EN CACHE LOS DATOS DEL MARKETPLACE  */

                    // Clean and set the vendors to cache
                    while (vendors.length) vendors.pop();
                    market.vendors.forEach(x => vendors.push(x));

                    // Clean and set the categories to cache
                    while (categories.length) categories.pop();
                    market.categories.forEach(x => categories.push(x));

                    // Clean and set the thematics to cache
                    //while (thematics.length) thematics.pop();
                    //market.thematics.forEach(x => thematics.push(x));

                    // Clean and set the stores to cache
                    while (stores.length) stores.pop();
                    market.stores.forEach(x => stores.push(x));

                    // Clean and set the giftcards to cache
                    while (giftcards.length) giftcards.pop();
                    market.giftcards.forEach(x => giftcards.push(x));

                    // Clean and set the products to cache
                    while (products.length) products.pop();
                    market.products.forEach(x => products.push(x));

                    // Clean and set the variables to cache
                    result.environment.forEach(x =>
                        envVariables[x.name] = x.value
                    );

                    /*  ESTO ES PARA MANTENER EN CACHE LOS DATOS DEL MARKETPLACE  */

                    return market;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err, null, true);

                    return Promise.reject(new Error(`Error al cargar información del marketplace: ${errMsg}`));
                }
            );
    }

    // GET api/admin/market/reads/v1/
    getPvcMarketBase(): Promise<PvcMarketBaseModel> {

        return this.http.get(`${this.baseUrl}/api/admin/market/pvc/reads/v1`, this.getHttpOptions())
            .toPromise().then(
                (result: any) => {

                    // JSON retornado por el servidor
                    const categoriesResult: any[] = result.categories.filter(x => x.isActive);
                    const thematicsResult: any[] = result.thematics.filter(x => x.isActive).sort((a, b) => a.ordinal - b.ordinal);
                    const zonesResult: any[] = result.zones.filter(x => x.isActive);
                    const eventsResult: any[] = result.events;
                    const passesResult: any[] = result.events.filter(x => x.type == EventType.PassWithExpirationDate || x.type == EventType.PassWithOutExpirationDate);
                    const cashlessResult: any[] = result.cashless;
                    const ticketsResult: any[] = result.tickets.filter(x => x.isVisible && x.isAvailable);
                    
                    const market = new PvcMarketBaseModel({
                        // Todos los objetos relacionados
                        categories: categoriesResult,
                        thematics: thematicsResult,
                        zones: zonesResult,
                        stores: result.stores,
                        vendors: result.vendors,
                        shippingProviders: result.shippingProviders,
                        // Todos los objetos principales
                        events: eventsResult,
                        passes: passesResult,
                        cashless: cashlessResult,
                        tickets: ticketsResult
                    });

                    /* OPTIMIZACIÓN: SETEAR LOS OBJETOS RELACIONADOS */
                    // Set the vendor to store
                    market.stores.forEach(x => {
                        x.vendor = market.vendors.find(v => v.id == x.vendorId);
                    });

                    // Set the vendor to store
                    market.events.forEach(x => {
                        x.vendor = market.vendors.find(v => v.id == x.vendorId);

                        // JSON retornado por el servidor
                        const eventObj = eventsResult.find(y => y.id == x.id);

                        // Adquirir los datos de la categories
                        x.categories = eventObj?.categoryIds?.map(id => {
                            const cat = market.categories.find(c => c.id == id);
                            return cat;
                        });

                        // Adquirir los datos de la temática
                        x.thematics = eventObj?.thematicIds.map(id => {
                            const cat = market.thematics.find(c => c.id == id);
                            return cat;
                        });

                        if (x.zoneId) {
                            // Adquirir los datos de la zona
                            x.zone = market.zones.find(z => z.id == x.zoneId);
                        }
                    });

                    // Set the vendor to ticket
                    market.tickets.forEach(x => {
                        if (x.shippingProviderId) x.shippingProvider = market.shippingProviders.find(s => s.id == x.shippingProviderId);
                        x.vendor = market.vendors.find(v => v.id == x.vendorId);
                        if (x.eventId) {
                            x.eventObj = market.events.find(s => s.id == x.eventId);
                        }

                        // JSON retornado por el servidor
                        const ticket = ticketsResult.find(y => y.id == x.id);

                        // Adquirir los datos de la categories
                        x.categories = ticket?.categoryIds?.map(id => {
                            const cat = market.categories.find(c => c.id == id);
                            return cat;
                        });

                        // Adquirir los datos de la temática
                        x.thematics = ticket?.thematicIds?.map(id => {
                            const cat = market.thematics.find(c => c.id == id);
                            return cat;
                        });

                        // Adquirir los datos de la tiendas
                        x.stores = ticket?.storeIds?.map(id => {
                            const sto = market.stores.find(s => s.id == id);
                            return sto;
                        });
                    });
                    /* OPTIMIZACIÓN: SETEAR LOS OBJETOS RELACIONADOS */


                    /*  ESTO ES PARA MANTENER EN CACHE LOS DATOS DEL MARKETPLACE  */

                    // Clean and set the vendors to cache
                    while (vendors.length) vendors.pop();
                    market.vendors.forEach(x => vendors.push(x));

                    // Clean and set the categories to cache
                    while (categories.length) categories.pop();
                    market.categories.forEach(x => categories.push(x));

                    // Clean and set the thematics to cache
                    while (thematics.length) thematics.pop();
                    market.thematics.forEach(x => thematics.push(x));

                    // Clean and set the thematics to cache
                    while (zones.length) zones.pop();
                    market.zones.forEach(x => zones.push(x));

                    // Clean and set the stores to cache
                    while (stores.length) stores.pop();
                    market.stores.forEach(x => stores.push(x));

                    // Clean and set the tickets to cache
                    while (tickets.length) tickets.pop();
                    market.tickets.forEach(x => tickets.push(x));

                    // Clean and set the tickets to cache
                    while (cashless.length) cashless.pop();
                    market.cashless.forEach(x => cashless.push(x));

                    /*  ESTO ES PARA MANTENER EN CACHE LOS DATOS DEL MARKETPLACE  */

                    return market;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err, null, true);

                    return Promise.reject(new Error(`Error al cargar información del marketplace: ${errMsg}`));
                }
            );
    }

    // GET api/admin/coupons/vendor/reads/v1/{id}/{userId}
    getAllCouponsByVendor(id: number, userId: number): Promise<CouponModel[]> {

        return this.http.get(`${this.baseUrl}/api/admin/market/coupons/vendor/reads/v1/${id}/${userId}`, this.getHttpOptions())
            .toPromise().then(
                (results: any) => {

                    // Todos los Bits por comercio
                    let allCoupons = results.map(x => {

                        return new CouponModel(x);
                    }).sort((a: CouponModel, b: CouponModel) => b.id - a.id);

                    return allCoupons;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de las órdenes: ${errMsg}`));
                }
            );
    }

    // GET api/admin/vendors/image/reads/v1/{id}
    getProfileImages(id: number): Promise<ProfileImageModel[]> {

        return this.http.get(`${this.baseUrl}/api/admin/vendors/image/reads/v1/${id}`, this.getHttpOptions())
            .toPromise().then(
                (results: any) => {

                    let allImages = results.map(x => {

                        return new ProfileImageModel(x);
                    }).sort((a, b) => a.name.localeCompare(b.name));

                    return allImages;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de los intereses: ${errMsg}`));
                }
            );
    }

    // GET: api/admin/tickets/vendor/reads/v1/{id}
    getMarketTicketsByVendor(id: number): Promise<TicketModel[]> {

        return this.http.get(`${this.baseUrl}/api/admin/tickets/vendor/reads/v1/${id}`, this.getHttpOptions())
            .toPromise().then(
                (results: any) => {

                    let tickets = results.map(x => new TicketModel(x));

                    return tickets;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de los intereses: ${errMsg}`));
                }
            );
    }
}
